import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Zap, Package, Eye, ArchiveRestore, Archive, ArrowRight, FolderOpen, Loader2, Plus, Lock,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PATH_META = {
  path_fast_cash: { name: 'Fast Cash', icon: Zap, tint: 'bg-emerald-100 text-emerald-700', slug: 'path_fast_cash' },
  path_digital_product: { name: 'Digital Product', icon: Package, tint: 'bg-violet-100 text-violet-700', slug: 'path_digital_product' },
  path_faceless_affiliate: { name: 'Faceless Affiliate', icon: Eye, tint: 'bg-amber-100 text-amber-700', slug: 'path_faceless_affiliate' },
};

const Projects = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [limits, setLimits] = useState({ max_active_projects: 1, active_count: 0, archived_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState('active');

  const fetchProjects = useCallback(async () => {
    const token = session?.access_token;
    if (!token) return;
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load projects');
      const data = await res.json();
      setProjects(data.projects || []);
      setLimits(data.limits || limits);
    } catch (e) {
      toast.error(e.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleArchive = async (project) => {
    setBusyId(project.id);
    try {
      const res = await fetch(`${API}/projects/${project.id}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error('Archive failed');
      toast.success(`"${project.name}" archived`);
      await fetchProjects();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleReactivate = async (project) => {
    setBusyId(project.id);
    try {
      const res = await fetch(`${API}/projects/${project.id}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail;
        if (res.status === 402) {
          toast.error(detail?.message || 'Upgrade required to reactivate this project');
          return;
        }
        throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Reactivate failed');
      }
      toast.success(`"${project.name}" reactivated`);
      await fetchProjects();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const openProject = (project) => {
    // Deep-link to path detail; user picks up at current step.
    navigate(`/path/${project.path_id}`);
  };

  const activeProjects = projects.filter((p) => p.status === 'active');
  const archivedProjects = projects.filter((p) => p.status === 'archived');
  const displayed = tab === 'active' ? activeProjects : archivedProjects;

  const isUnlimited = limits.max_active_projects === -1;
  const atLimit = !isUnlimited && limits.active_count >= limits.max_active_projects;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <AppLayout maxWidth="max-w-6xl">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" data-testid="projects-title">
            My Projects
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Every product you build is a project. Archive to make room for new ideas.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="px-3 py-1.5 rounded-full bg-secondary border border-border" data-testid="projects-limits">
            <span className="font-semibold text-foreground">{limits.active_count}</span>
            <span className="text-muted-foreground"> / </span>
            <span className="text-muted-foreground">
              {isUnlimited ? '∞' : limits.max_active_projects} active
            </span>
          </div>
          {atLimit && (
            <Link to="/pricing">
              <Button
                variant="outline"
                size="sm"
                className="border-teal-500 text-teal-700 hover:bg-teal-50"
                data-testid="projects-upgrade-cta"
              >
                <Zap className="h-4 w-4 mr-1.5" /> Upgrade
              </Button>
            </Link>
          )}
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('active')}
          className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
            tab === 'active'
              ? 'border-teal-600 text-teal-700'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="projects-tab-active"
        >
          Active
          <span className="ml-2 text-xs bg-secondary text-muted-foreground px-1.5 py-0.5 rounded-full">
            {activeProjects.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setTab('archived')}
          className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
            tab === 'archived'
              ? 'border-teal-600 text-teal-700'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="projects-tab-archived"
        >
          Archived
          <span className="ml-2 text-xs bg-secondary text-muted-foreground px-1.5 py-0.5 rounded-full">
            {archivedProjects.length}
          </span>
        </button>
      </div>

      {/* Empty state */}
      {displayed.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-14 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mb-4">
              <FolderOpen className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {tab === 'active' ? 'No active projects yet' : 'No archived projects'}
            </h3>
            <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
              {tab === 'active'
                ? 'Start a new path from your dashboard and it will show up here as a project.'
                : 'Archived projects appear here. Reactivate them anytime (subject to your plan limits).'}
            </p>
            {tab === 'active' && (
              <Link to="/dashboard">
                <Button className="bg-creataby-gradient-btn text-white hover:opacity-95" data-testid="projects-empty-cta">
                  <Plus className="h-4 w-4 mr-1.5" /> Start a Project
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayed.map((p, idx) => {
            const pathMeta = PATH_META[p.path_id] || { name: p.path_name || 'Path', icon: FolderOpen, tint: 'bg-slate-100 text-slate-700' };
            const Icon = pathMeta.icon;
            const isArchived = p.status === 'archived';
            const reactivateBlocked = isArchived && atLimit;

            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                whileHover={{ y: -2 }}
              >
                <Card
                  className={`h-full flex flex-col border border-border rounded-lg overflow-hidden transition-all creataby-card-hover ${
                    isArchived ? 'opacity-80' : ''
                  }`}
                  data-testid={`project-card-${p.id}`}
                >
                  <CardContent className="p-5 flex-1 flex flex-col">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${pathMeta.tint}`}
                      >
                        <Icon className="h-3 w-3" />
                        {pathMeta.name}
                      </span>
                      {isArchived && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                          <Archive className="h-3 w-3" /> Archived
                        </span>
                      )}
                    </div>

                    <h3 className="text-base font-semibold text-foreground mb-1 line-clamp-2">
                      {p.name}
                    </h3>
                    {p.product_type && (
                      <p className="text-xs text-muted-foreground mb-3 capitalize">
                        {p.product_type.replace(/_/g, ' ')}
                      </p>
                    )}

                    {/* Progress */}
                    <div className="mt-auto pt-3">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-muted-foreground">
                          {p.completed_steps} of {p.total_steps} steps
                        </span>
                        <span className="font-semibold text-foreground">{p.progress_pct}%</span>
                      </div>
                      <Progress value={p.progress_pct} className="h-1.5" />
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex items-center gap-2">
                      {isArchived ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-border"
                          onClick={() => handleReactivate(p)}
                          disabled={busyId === p.id || reactivateBlocked}
                          data-testid={`project-reactivate-${p.id}`}
                          title={reactivateBlocked ? 'Upgrade to reactivate — you are at your active project limit' : undefined}
                        >
                          {busyId === p.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : reactivateBlocked ? (
                            <><Lock className="h-4 w-4 mr-1.5" /> Locked</>
                          ) : (
                            <><ArchiveRestore className="h-4 w-4 mr-1.5" /> Reactivate</>
                          )}
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            className="flex-1 bg-foreground text-background hover:bg-foreground/90"
                            onClick={() => openProject(p)}
                            data-testid={`project-open-${p.id}`}
                          >
                            Continue <ArrowRight className="h-4 w-4 ml-1.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => handleArchive(p)}
                            disabled={busyId === p.id}
                            data-testid={`project-archive-${p.id}`}
                            title="Archive"
                          >
                            {busyId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
};

export default Projects;
